import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from 'Hooks/useDarkMode';

import { createServerSideDatasource } from 'Components/AgGrid/createServerSideDatasource';
import { defaultColDef, defaultGridProps } from 'Components/AgGrid/defaultGridConfig';

/**
 * Base AgGrid table component parametrized by endpoint, columns, and route.
 *
 * @param {Object} props
 * @param {string} props.endpoint - API endpoint for server-side data
 * @param {string} props.viewName - View name for logging
 * @param {string} props.statusPath - URL path for row navigation (use 'all' to read from row data)
 * @param {Array|Function} props.columns - Column definitions array, or a function
 *   receiving (gridRef, datasourceFactory, dispatch) that returns column definitions.
 *   Use the function form when action columns need grid refresh capabilities.
 * @param {string} [props.roleToCheckForActions] - Removes last column when user lacks this role
 * @param {Function} [props.getRowStyle] - Optional row style function
 * @param {Object} [props.additionalGridProps] - Extra props for AgGridReact
 */
const BaseAgGridTable = ({
  endpoint,
  viewName,
  statusPath,
  columns,
  roleToCheckForActions,
  getRowStyle,
  additionalGridProps,
}) => {
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const { agGridTheme } = useDarkMode();

  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles;

  /**
   * Creates a datasource factory compatible with the old (dispatch, gridRef) signature
   * used by helpers like handleDeleteFlightTicket and setGreenLight.
   */
  const legacyDatasourceFactory = useCallback(
    (d, ref) => createServerSideDatasource(endpoint, viewName, d, ref),
    [endpoint, viewName]
  );

  const resolvedColumns = useMemo(
    () => (typeof columns === 'function' ? columns(gridRef, legacyDatasourceFactory, dispatch) : columns),
    [columns, legacyDatasourceFactory, dispatch]
  );

  const [columnDefs, setColumnDefs] = useState(resolvedColumns);

  useEffect(() => {
    setColumnDefs(resolvedColumns);
  }, [resolvedColumns]);

  useEffect(() => {
    if (roleToCheckForActions && userRoles?.length > 0 && !userRoles.includes(roleToCheckForActions)) {
      setColumnDefs((prev) => prev.slice(0, -1));
    }
  }, [userRoles, roleToCheckForActions]);

  const onGridReady = useCallback(
    (params) => {
      params.api.updateGridOptions({
        serverSideDatasource: createServerSideDatasource(endpoint, viewName, dispatch, gridRef),
      });
    },
    [endpoint, viewName, dispatch]
  );

  const onRowDoubleClicked = useCallback(
    (event) => {
      const path = statusPath === 'all' ? event.data.status : statusPath;
      const url = `${location.protocol}//${location.host}/${path}/${event.data.id}`;
      window.open(url, '_blank');
    },
    [statusPath]
  );

  return (
    <React.Fragment>
      <div className="w-100 h-100">
        <div className={`${agGridTheme} ag-grid-responsive`}>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onRowDoubleClicked={onRowDoubleClicked}
            getRowStyle={getRowStyle}
            {...defaultGridProps}
            {...additionalGridProps}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default BaseAgGridTable;
